import { useCallback, useEffect, useState } from 'react';
import { api } from '../lib/api';
import type { ZoneRow } from '../lib/types';
import { money } from '../lib/format';
import { Money } from '@delivery/shared';
import { CustomerZonePrices } from '../components/CustomerZonePrices';
import { PostcodePrices } from '../components/PostcodePrices';
import { X } from 'lucide-react';
import { InlineIcon } from '../components/InlineIcon';

/**
 * 计价规则。
 *
 * ★ 这一页的核心不是那张表格,是右边的**试算**。
 *   一屏 20 个数字输入框,谁也说不清改完之后一单收多少;
 *   而真正发现算错的时机会是客户收到账单之后。
 *   所以「填几个数字,当场看结果」必须和编辑放在同一屏。
 */

interface Tier {
  upToGram?: number | null;
  upToMeter?: number | null;
  pricePerKg?: number;
  pricePerKm?: number;
}

interface Rule {
  id: string;
  zone: { id: string; name: string } | null;
  customer: { id: string; name: string } | null;
  scope: string;
  specificity: number;
  currency: string;
  basePrice: string;
  includedWeightGram: number;
  weightTiers: Tier[];
  extraPackagePrice: string;
  distanceTiers: Tier[];
  minCharge: string;
  codFeeRate: number;
  taxRate: number;
  driverPayoutMode: 'FIXED' | 'PERCENT' | 'PER_PACKAGE';
  driverPayoutValue: string;
  effectiveFrom: string;
  effectiveTo: string | null;
  isActive: boolean;
}

interface QuoteResult {
  matched: boolean;
  reason?: string;
  customerPrice?: number;
  driverPayout?: number;
  breakdown?: { lines: Array<{ code: string; label: string; amount: number }> };
  rule?: { id: string | null; scope: string };
  /** 命中了「邮编运费」里的某个区间 */
  postcodePrice?: { id: string; postcode: string; mode: 'FIXED' | 'REMOTE'; price: number; includeExtras: boolean } | null;
}

const PAYOUT_LABEL: Record<Rule['driverPayoutMode'], string> = {
  FIXED: '固定金额',
  PERCENT: '按配送费抽成',
  PER_PACKAGE: '按件计酬',
};

export function Pricing({
  zones,
  permissions,
}: {
  zones: ZoneRow[];
  permissions: string[];
}) {
  const canManage = permissions.includes('pricing:manage');
  const [rules, setRules] = useState<Rule[]>([]);
  const [editing, setEditing] = useState<Rule | null>(null);
  const [err, setErr] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      setRules(await api<Rule[]>('/pricing/rules'));
    } catch (e) {
      setErr(e instanceof Error ? e.message : '加载失败');
    }
  }, []);
  useEffect(() => void load(), [load]);

  return (
    <div className="p-4 space-y-4 lg:p-6">
      <header className="flex items-center justify-between">
        <div>
          <h1 className="text-lg font-semibold text-slate-900 dark:text-slate-100">计价规则</h1>
          <p className="text-sm text-slate-500 mt-0.5">
            越具体的规则越优先:某商家 + 某区域 &gt; 某商家 &gt; 某区域 &gt; 通用
          </p>
        </div>
        {canManage && (
          <button
            onClick={() => setEditing(blankRule())}
            className="px-3 py-2 rounded-lg bg-brand-600 text-white text-sm hover:bg-brand-700"
          >
            新建规则
          </button>
        )}
      </header>

      {/*
        ★ 「每家在每个大区收多少」放在最上面。
          它是这一页上**用得最多**的东西:规则列表管的是重量阶梯、抽成这些
          一次配好就不动的参数,而分区价是跟着每个客户谈出来的,
          隔三差五就要改一格。
      */}
      <CustomerZonePrices canManage={canManage} />

      <PostcodePrices canManage={canManage} />


      {err && <div className="rounded-lg bg-red-50 text-red-700 px-3 py-2 text-sm">{err}</div>}

      <div className="grid grid-cols-1 xl:grid-cols-[1fr_360px] gap-4">
        <div className="rounded-xl border border-slate-200 dark:border-slate-700 overflow-x-auto">
          <table className="w-full min-w-[640px] text-sm">
            <thead className="bg-slate-50 dark:bg-slate-800 text-slate-500">
              <tr>
                <th className="text-left px-3 py-2 font-medium">适用范围</th>
                <th className="text-left px-3 py-2 font-medium">起步价</th>
                <th className="text-left px-3 py-2 font-medium">司机酬劳</th>
                <th className="text-left px-3 py-2 font-medium">生效</th>
                <th className="px-3 py-2" />
              </tr>
            </thead>
            <tbody>
              {rules.length === 0 && (
                <tr>
                  <td colSpan={5} className="px-3 py-8 text-center text-slate-400">
                    一条规则都没有 —— 这样建出来的单运费会是 0,要人工填
                  </td>
                </tr>
              )}
              {rules.map((r) => (
                <tr
                  key={r.id}
                  className={`border-t border-slate-100 dark:border-slate-700/60 ${
                    r.isActive ? '' : 'opacity-50'
                  }`}
                >
                  <td className="px-3 py-2">
                    <span className="text-slate-800 dark:text-slate-200">{r.scope}</span>
                    {!r.isActive && <span className="ml-2 text-xs text-slate-400">已停用</span>}
                  </td>
                  <td className="px-3 py-2 font-mono">{money(r.basePrice, r.currency)}</td>
                  <td className="px-3 py-2">
                    {r.driverPayoutMode === 'PERCENT'
                      ? `${Number(r.driverPayoutValue) / 100}%`
                      : money(r.driverPayoutValue, r.currency)}
                    <span className="ml-1 text-xs text-slate-400">
                      {PAYOUT_LABEL[r.driverPayoutMode]}
                    </span>
                  </td>
                  <td className="px-3 py-2 text-slate-500 text-xs">
                    {r.effectiveFrom.slice(0, 10)}
                    {r.effectiveTo ? ` ~ ${r.effectiveTo.slice(0, 10)}` : ' 起'}
                  </td>
                  <td className="px-3 py-2 text-right">
                    {canManage && (
                      <button
                        onClick={() => setEditing(r)}
                        className="text-brand-600 hover:underline"
                      >
                        编辑
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <QuotePanel zones={zones} />
      </div>

      {editing && (
        <RuleEditor
          rule={editing}
          zones={zones}
          onClose={() => setEditing(null)}
          onSaved={async () => {
            setEditing(null);
            await load();
          }}
        />
      )}
    </div>
  );
}

/** 试算面板 —— 填几个数字,当场看这一单收多少、司机拿多少 */
function QuotePanel({ zones }: { zones: ZoneRow[] }) {
  const [form, setForm] = useState({ zoneId: '', postcode: '', weightGram: 3000, packageCount: 1, codAmount: 0 });
  /** 代收金额按元填 —— 输入框存原文,`form.codAmount` 存最小单位 */
  const [codMajor, setCodMajor] = useState('');
  const [res, setRes] = useState<QuoteResult | null>(null);
  const [busy, setBusy] = useState(false);

  const run = async () => {
    setBusy(true);
    try {
      setRes(
        await api<QuoteResult>('/pricing/rules/quote', {
          method: 'POST',
          body: {
            zoneId: form.zoneId || null,
            receiverPostcode: form.postcode.trim() || null,
            weightGram: form.weightGram,
            packageCount: form.packageCount,
            codAmount: form.codAmount,
          },
        }),
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="rounded-xl border border-slate-200 dark:border-slate-700 p-4 space-y-3 h-fit">
      <div className="font-medium text-slate-800 dark:text-slate-200">试算</div>
      <p className="text-xs text-slate-500">
        按几个数字算一遍,看看命中的是哪条规则、客户收多少、司机拿多少
      </p>

      <label className="block text-sm">
        <span className="text-slate-500">区域</span>
        <select
          value={form.zoneId}
          onChange={(e) => setForm({ ...form, zoneId: e.target.value })}
          className="mt-1 w-full rounded-lg border border-slate-300 dark:border-slate-600
                     bg-white dark:bg-slate-800 px-2 py-1.5"
        >
          <option value="">不限(走通用规则)</option>
          {zones.map((z) => (
            <option key={z.id} value={z.id}>
              {z.name}
            </option>
          ))}
        </select>
      </label>

      <label className="block text-sm">
        <span className="text-slate-500">收件邮编</span>
        <input
          inputMode="numeric"
          aria-label="收件邮编"
          value={form.postcode}
          onChange={(e) => setForm({ ...form, postcode: e.target.value })}
          placeholder="不填 = 不按邮编算"
          className="mt-1 w-full rounded-lg border border-slate-300 dark:border-slate-600
                     bg-white dark:bg-slate-800 px-2 py-1.5"
        />
      </label>

      <NumField
        label="重量(克)"
        value={form.weightGram}
        onChange={(v) => setForm({ ...form, weightGram: v })}
      />
      <NumField
        label="件数"
        value={form.packageCount}
        onChange={(v) => setForm({ ...form, packageCount: Math.max(1, v) })}
      />
      {/*
        ★ 代收金额按**元**填 —— 和建单页、订单详情统一。
          同一个数在一处填元、另一处填分,人迟早会在其中一处差 100 倍,
          而代收金额差 100 倍是司机在门口才发现的事。
          试算接口收的是最小单位,换算在这一处做。
      */}
      <label className="block text-sm">
        <span className="text-slate-500">代收金额(元)</span>
        <input
          inputMode="decimal"
          aria-label="代收金额(元)"
          value={codMajor}
          onChange={(e) => {
            setCodMajor(e.target.value);
            setForm({
              ...form,
              // ★ 敲到一半的「12.」不能抛 —— 见 Money.tryFromMajor 的说明
              codAmount: Money.tryFromMajor(e.target.value) ?? 0,
            });
          }}
          className="mt-1 w-full rounded-lg border border-slate-300 dark:border-slate-600
                     bg-white dark:bg-slate-800 px-2 py-1.5"
        />
      </label>

      <button
        onClick={run}
        disabled={busy}
        className="w-full px-3 py-2 rounded-lg bg-slate-800 dark:bg-slate-700 text-white text-sm
                   hover:bg-slate-900 disabled:opacity-50"
      >
        {busy ? '算…' : '试算'}
      </button>

      {res && !res.matched && (
        /*
          ★ 算不出来时说清楚为什么。
            显示「运费 0.00」的话看起来像「这单免费」,
            而真相是「一条规则都没匹配上」。
        */
        <div className="rounded-lg bg-amber-50 dark:bg-amber-500/10 px-3 py-2 text-sm text-amber-800 dark:text-amber-300">
          {res.reason}
        </div>
      )}

      {res?.matched && (
        /*
          ★ 这一块标出来(aria-label)。
            明细里的行名和左边那张规则表的表头是同一批词
            (「起步价」在两处都有)—— 不圈出范围的话,
            连用例都分不清自己看的是表头还是试算结果。
        */
        <section
          aria-label="试算结果"
          className="rounded-lg bg-slate-50 dark:bg-slate-800/60 p-3 text-sm space-y-1"
        >
          <div className="text-xs text-slate-400">命中规则:{res.rule?.scope}</div>
          {res.postcodePrice && (
            <div className="text-xs text-slate-400">
              {res.postcodePrice.mode === 'REMOTE'
                ? `邮编 ${res.postcodePrice.postcode} 按偏远地区加价`
                : `按邮编 ${res.postcodePrice.postcode} 的具体运费算${
                    res.postcodePrice.includeExtras ? '(超重 / 多件 / 代收另加)' : ''
                  }`}
            </div>
          )}
          {res.breakdown?.lines.map((l, i) => (
            <div key={`${l.code}-${i}`} className="flex justify-between">
              <span className="text-slate-500">{l.label}</span>
              <span className="font-mono">{money(String(l.amount))}</span>
            </div>
          ))}
          <div className="flex justify-between border-t border-slate-200 dark:border-slate-700 pt-1 font-medium">
            <span>客户付</span>
            <span className="font-mono">{money(String(res.customerPrice ?? 0))}</span>
          </div>
          <div className="flex justify-between text-brand-700 dark:text-brand-400">
            <span>司机拿</span>
            <span className="font-mono">{money(String(res.driverPayout ?? 0))}</span>
          </div>
        </section>
      )}
    </div>
  );
}

function RuleEditor({
  rule,
  zones,
  onClose,
  onSaved,
}: {
  rule: Rule;
  zones: ZoneRow[];
  onClose: () => void;
  onSaved: () => void;
}) {
  const [f, setF] = useState(rule);
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const isNew = f.id === '';

  const save = async () => {
    setBusy(true);
    setErr(null);
    try {
      const body = {
        zoneId: f.zone?.id ?? null,
        customerId: f.customer?.id ?? null,
        serviceLevel: 'STANDARD',
        basePrice: Number(f.basePrice),
        includedWeightGram: f.includedWeightGram,
        weightTiers: f.weightTiers,
        extraPackagePrice: Number(f.extraPackagePrice),
        distanceTiers: f.distanceTiers,
        minCharge: Number(f.minCharge),
        codFeeRate: f.codFeeRate,
        taxRate: f.taxRate,
        driverPayoutMode: f.driverPayoutMode,
        driverPayoutValue: Number(f.driverPayoutValue),
        effectiveFrom: f.effectiveFrom,
        effectiveTo: f.effectiveTo,
        isActive: f.isActive,
      };
      if (isNew) await api('/pricing/rules', { method: 'POST', body });
      else await api(`/pricing/rules/${f.id}`, { method: 'PUT', body });
      onSaved();
    } catch (e) {
      setErr(e instanceof Error ? e.message : '保存失败');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 grid place-items-center p-4 z-50" onClick={onClose}>
      <div
        className="bg-white dark:bg-slate-800 rounded-xl w-full max-w-lg max-h-[90dvh] overflow-auto p-5 space-y-3"
        onClick={(e) => e.stopPropagation()}
      >
        <h2 className="font-semibold text-slate-900 dark:text-slate-100">
          {isNew ? '新建计价规则' : '编辑计价规则'}
        </h2>

        <label className="block text-sm">
          <span className="text-slate-500">适用区域</span>
          <select
            value={f.zone?.id ?? ''}
            onChange={(e) => {
              const z = zones.find((x) => x.id === e.target.value);
              setF({ ...f, zone: z ? { id: z.id, name: z.name } : null });
            }}
            className="mt-1 w-full rounded-lg border border-slate-300 dark:border-slate-600
                       bg-white dark:bg-slate-800 px-2 py-1.5"
          >
            <option value="">所有区域(兜底规则)</option>
            {zones.map((z) => (
              <option key={z.id} value={z.id}>
                {z.name}
              </option>
            ))}
          </select>
        </label>

        <NumField label="起步价(分)" value={Number(f.basePrice)} onChange={(v) => setF({ ...f, basePrice: String(v) })} />
        <NumField
          label="起步价含重量(克)"
          value={f.includedWeightGram}
          onChange={(v) => setF({ ...f, includedWeightGram: v })}
        />
        <NumField
          label="每多一件加(分)"
          value={Number(f.extraPackagePrice)}
          onChange={(v) => setF({ ...f, extraPackagePrice: String(v) })}
        />
        <NumField label="最低消费(分)" value={Number(f.minCharge)} onChange={(v) => setF({ ...f, minCharge: String(v) })} />

        <TierEditor
          label="续重阶梯"
          hint="超出免费重量的部分,按公斤计价;最后一档留空表示不限"
          tiers={f.weightTiers}
          onChange={(weightTiers) => setF({ ...f, weightTiers })}
        />

        <label className="block text-sm">
          <span className="text-slate-500">司机酬劳方式</span>
          <select
            value={f.driverPayoutMode}
            onChange={(e) => setF({ ...f, driverPayoutMode: e.target.value as Rule['driverPayoutMode'] })}
            className="mt-1 w-full rounded-lg border border-slate-300 dark:border-slate-600
                       bg-white dark:bg-slate-800 px-2 py-1.5"
          >
            {(['FIXED', 'PERCENT', 'PER_PACKAGE'] as const).map((m) => (
              <option key={m} value={m}>
                {PAYOUT_LABEL[m]}
              </option>
            ))}
          </select>
        </label>
        <NumField
          label={
            f.driverPayoutMode === 'PERCENT' ? '抽成(万分比,7000 = 70%)' : '司机酬劳(分)'
          }
          value={Number(f.driverPayoutValue)}
          onChange={(v) => setF({ ...f, driverPayoutValue: String(v) })}
        />
        {f.driverPayoutMode === 'PERCENT' && (
          <p className="text-xs text-slate-500 -mt-1">
            抽成按**税前、不含代收手续费**的配送费算 —— 税和手续费不是这一趟的运费
          </p>
        )}

        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={f.isActive}
            onChange={(e) => setF({ ...f, isActive: e.target.checked })}
          />
          <span>启用</span>
        </label>

        {err && <div className="rounded-lg bg-red-50 text-red-700 px-3 py-2 text-sm">{err}</div>}

        <p className="text-xs text-slate-400">
          改价只影响之后建的单 —— 已经建好的单存着当时的明细,不会跟着变
        </p>

        <div className="flex justify-end gap-2 pt-1">
          <button onClick={onClose} className="px-3 py-2 rounded-lg text-slate-500 text-sm">
            取消
          </button>
          <button
            onClick={save}
            disabled={busy}
            className="px-3 py-2 rounded-lg bg-brand-600 text-white text-sm hover:bg-brand-700 disabled:opacity-50"
          >
            {busy ? '保存中…' : '保存'}
          </button>
        </div>
      </div>
    </div>
  );
}

function TierEditor({
  label,
  hint,
  tiers,
  onChange,
}: {
  label: string;
  hint: string;
  tiers: Tier[];
  onChange: (t: Tier[]) => void;
}) {
  return (
    <div className="text-sm">
      <div className="text-slate-500">{label}</div>
      <p className="text-xs text-slate-400 mb-1">{hint}</p>
      {tiers.map((t, i) => (
        <div key={i} className="flex items-center gap-2 mb-1">
          <input
            type="number"
            aria-label={`第 ${i + 1} 档上界(克)`}
            value={t.upToGram ?? ''}
            placeholder="不限"
            onChange={(e) => {
              const next = [...tiers];
              next[i] = { ...t, upToGram: e.target.value === '' ? null : Number(e.target.value) };
              onChange(next);
            }}
            className="w-28 rounded-lg border border-slate-300 dark:border-slate-600
                       bg-white dark:bg-slate-800 px-2 py-1"
          />
          <span className="text-xs text-slate-400">克以内,每公斤</span>
          <input
            type="number"
            aria-label={`第 ${i + 1} 档每公斤价格(分)`}
            value={t.pricePerKg ?? 0}
            onChange={(e) => {
              const next = [...tiers];
              next[i] = { ...t, pricePerKg: Number(e.target.value) };
              onChange(next);
            }}
            className="w-24 rounded-lg border border-slate-300 dark:border-slate-600
                       bg-white dark:bg-slate-800 px-2 py-1"
          />
          <button
            onClick={() => onChange(tiers.filter((_, j) => j !== i))}
            className="text-slate-400 hover:text-red-600"
            aria-label={`删掉第 ${i + 1} 档`}
          >
            <InlineIcon icon={X} />
          </button>
        </div>
      ))}
      <button
        onClick={() => onChange([...tiers, { upToGram: null, pricePerKg: 0 }])}
        className="text-brand-600 text-xs hover:underline"
      >
        + 加一档
      </button>
    </div>
  );
}

function NumField({
  label,
  value,
  onChange,
}: {
  label: string;
  value: number;
  onChange: (v: number) => void;
}) {
  return (
    <label className="block text-sm">
      <span className="text-slate-500">{label}</span>
      <input
        type="number"
        value={value}
        onChange={(e) => onChange(Number(e.target.value) || 0)}
        className="mt-1 w-full rounded-lg border border-slate-300 dark:border-slate-600
                   bg-white dark:bg-slate-800 px-2 py-1.5"
      />
    </label>
  );
}

function blankRule(): Rule {
  return {
    id: '',
    zone: null,
    customer: null,
    scope: '',
    specificity: 0,
    currency: 'NZD',
    basePrice: '0',
    includedWeightGram: 0,
    weightTiers: [],
    extraPackagePrice: '0',
    distanceTiers: [],
    minCharge: '0',
    codFeeRate: 0,
    taxRate: 0,
    driverPayoutMode: 'FIXED',
    driverPayoutValue: '0',
    // 立刻生效 —— 新建规则的人想的是「从现在起」
    effectiveFrom: new Date().toISOString(),
    effectiveTo: null,
    isActive: true,
  };
}
